import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../../hooks/useSettings';
import { useInView } from '../../../hooks/useInView';
import type { Vehiculo } from '../../../hooks/useSettings';
import VehicleDetailModal from './VehicleDetailModal';

type ActiveSection = 'compraventa' | 'alquiler';

const VehicleCard = ({
  v,
  delay,
  onClick,
}: {
  v: Vehiculo;
  delay: number;
  onClick: () => void;
}) => {
  const [cardRef, cardInView] = useInView(0.1);
  const hasOffer = v.oferta_activa && v.precio_oferta && v.precio_oferta > 0;

  return (
    <div
      ref={cardRef}
      onClick={onClick}
      className={`group bg-zinc-900/60 border border-zinc-800 hover:border-yellow-400/40 transition-all duration-500 overflow-hidden cursor-pointer ${
        cardInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}s`, transitionDuration: '600ms' }}
    >
      <div className="relative h-52 overflow-hidden bg-zinc-800">
        {v.imagen ? (
          <img
            alt={v.nombre}
            src={v.imagen}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700">
            <i className={`${v.tipo === 'moto' ? 'ri-motorbike-line' : 'ri-car-line'} text-5xl`} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Photo count badge */}
        {(v.imagenes?.length ?? 0) > 0 && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/70 text-white text-[10px] font-bold px-2 py-1">
            <i className="ri-image-line text-xs" />
            {(v.imagenes?.length ?? 0) + 1} fotos
          </div>
        )}

        <div className="absolute top-3 left-3 flex gap-1.5">
          {hasOffer && (
            <span className="text-xs font-bold uppercase px-2 py-0.5 bg-red-500 text-white">
              Oferta
            </span>
          )}
          {!v.disponible && (
            <span className="text-xs font-bold uppercase px-2 py-0.5 bg-zinc-700 text-zinc-300">
              Reservado
            </span>
          )}
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-black/60 text-zinc-300 capitalize">
            {v.tipo}
          </span>
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-yellow-400/0 group-hover:bg-yellow-400/5 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-yellow-400 text-black text-xs font-black uppercase px-4 py-2 flex items-center gap-2">
            <i className="ri-eye-line" /> Ver detalle
          </div>
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-white font-bold text-sm mb-3 leading-tight">{v.nombre}</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-4">
          {v.anio && (
            <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
              <i className="ri-calendar-line text-zinc-600" />
              {v.anio}
            </div>
          )}
          {v.km !== undefined && (
            <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
              <i className="ri-road-map-line text-zinc-600" />
              {v.km.toLocaleString('es-ES')} km
            </div>
          )}
          {v.combustible && (
            <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
              <i className="ri-gas-station-line text-zinc-600" />
              {v.combustible}
            </div>
          )}
          {v.potencia && (
            <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
              <i className="ri-flashlight-line text-zinc-600" />
              {v.potencia} CV
            </div>
          )}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
          <div>
            {hasOffer ? (
              <div>
                <span className="text-zinc-500 text-xs line-through mr-1.5">
                  {Number(v.precio).toLocaleString('es-ES')} €
                </span>
                <span className="font-display text-2xl text-yellow-400 tracking-wide">
                  {Number(v.precio_oferta).toLocaleString('es-ES')} €
                </span>
              </div>
            ) : (
              <span className="font-display text-2xl text-white tracking-wide">
                {Number(v.precio).toLocaleString('es-ES')} €
              </span>
            )}
          </div>
          <span className="inline-flex items-center gap-1.5 bg-zinc-800 text-zinc-400 text-xs font-bold uppercase px-3 py-2 group-hover:bg-yellow-400 group-hover:text-black transition-all whitespace-nowrap">
            Ver más <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </div>
  );
};

const RentalCard = ({
  v,
  delay,
  onClick,
}: {
  v: Vehiculo;
  delay: number;
  onClick: () => void;
}) => {
  const [cardRef, cardInView] = useInView(0.1);

  return (
    <div
      ref={cardRef}
      onClick={onClick}
      className={`group bg-zinc-900/60 border border-zinc-800 hover:border-yellow-400/30 transition-all duration-500 overflow-hidden cursor-pointer ${
        cardInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}s`, transitionDuration: '600ms' }}
    >
      <div className="relative h-52 overflow-hidden bg-zinc-800">
        {v.imagen ? (
          <img
            alt={v.nombre}
            src={v.imagen}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700">
            <i className="ri-car-line text-5xl" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-3 right-3 bg-yellow-400 text-black font-black text-sm px-3 py-1.5 leading-none">
          {v.precio_dia ?? '—'} €<span className="text-[10px] font-bold">/día</span>
        </div>
        {!v.disponible && (
          <div className="absolute top-3 left-3">
            <span className="text-xs font-bold uppercase px-2 py-0.5 bg-zinc-700 text-zinc-300">
              No disponible
            </span>
          </div>
        )}
        {(v.imagenes?.length ?? 0) > 0 && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/70 text-white text-[10px] font-bold px-2 py-1">
            <i className="ri-image-line text-xs" />
            {(v.imagenes?.length ?? 0) + 1} fotos
          </div>
        )}
        <div className="absolute inset-0 bg-yellow-400/0 group-hover:bg-yellow-400/5 transition-all duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-yellow-400 text-black text-xs font-black uppercase px-4 py-2 flex items-center gap-2">
            <i className="ri-eye-line" /> Ver detalle
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-white font-bold text-sm leading-tight">{v.nombre}</h3>
          <span className="flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 bg-yellow-400/10 text-yellow-400 border border-yellow-400/20 whitespace-nowrap ml-2">
            <i className="ri-key-line" /> Alquiler
          </span>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-4">
          {v.anio && (
            <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
              <i className="ri-calendar-line text-zinc-600" />
              {v.anio}
            </div>
          )}
          {v.km !== undefined && (
            <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
              <i className="ri-road-map-line text-zinc-600" />
              {v.km.toLocaleString('es-ES')} km
            </div>
          )}
          {v.combustible && (
            <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
              <i className="ri-gas-station-line text-zinc-600" />
              {v.combustible}
            </div>
          )}
        </div>

        {v.descripcion && (
          <p className="text-zinc-600 text-xs leading-relaxed mb-4 line-clamp-2">
            {v.descripcion}
          </p>
        )}

        <div className="pt-3 border-t border-zinc-800">
          <span className="w-full inline-flex items-center justify-center gap-2 bg-zinc-800 text-zinc-400 text-xs font-bold uppercase px-4 py-2.5 group-hover:bg-yellow-400 group-hover:text-black transition-all whitespace-nowrap">
            <i className="ri-eye-line" /> Ver detalle
          </span>
        </div>
      </div>
    </div>
  );
};

const CarsListing = () => {
  const { settings, loading } = useSettings();
  const [activeSection, setActiveSection] = useState<ActiveSection>('compraventa');
  const [sortBy, setSortBy] = useState('default');
  const [tipoFilter, setTipoFilter] = useState<'todos' | 'compra' | 'moto'>('todos');
  const [selectedVehiculo, setSelectedVehiculo] = useState<Vehiculo | null>(null);
  const [headerRef, headerInView] = useInView();

  const allVehiculos = settings.vehiculos.filter((v) => v.disponible !== false);
  const compraventaVehs = allVehiculos.filter((v) => v.tipo !== 'alquiler');
  const alquilerVehs = allVehiculos.filter((v) => v.tipo === 'alquiler');

  const filteredCompraventa = compraventaVehs
    .filter((v) => tipoFilter === 'todos' || v.tipo === tipoFilter)
    .sort((a, b) => {
      const pa = a.oferta_activa && a.precio_oferta ? a.precio_oferta : a.precio;
      const pb = b.oferta_activa && b.precio_oferta ? b.precio_oferta : b.precio;
      if (sortBy === 'price-asc') return pa - pb;
      if (sortBy === 'price-desc') return pb - pa;
      if (sortBy === 'km') return (a.km ?? 0) - (b.km ?? 0);
      return 0;
    });

  if (loading) {
    return (
      <section className="bg-zinc-950 py-20 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-center h-40">
          <i className="ri-loader-4-line animate-spin text-yellow-400 text-2xl" />
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="bg-zinc-950 py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Section switcher */}
          <div
            ref={headerRef}
            className={`mb-10 transition-all duration-700 ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
              <div className="flex border border-zinc-800 overflow-hidden">
                <button
                  onClick={() => setActiveSection('compraventa')}
                  className={`flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-all cursor-pointer whitespace-nowrap ${
                    activeSection === 'compraventa'
                      ? 'bg-yellow-400 text-black'
                      : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  <i className="ri-car-line" />
                  Compraventa
                  {compraventaVehs.length > 0 && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-black ${
                        activeSection === 'compraventa'
                          ? 'bg-black/20 text-black'
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {compraventaVehs.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setActiveSection('alquiler')}
                  className={`flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-all cursor-pointer whitespace-nowrap border-l border-zinc-800 ${
                    activeSection === 'alquiler'
                      ? 'bg-yellow-400 text-black'
                      : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  <i className="ri-key-line" />
                  Alquiler
                  {alquilerVehs.length > 0 && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-black ${
                        activeSection === 'alquiler'
                          ? 'bg-black/20 text-black'
                          : 'bg-zinc-800 text-zinc-400'
                      }`}
                    >
                      {alquilerVehs.length}
                    </span>
                  )}
                </button>
              </div>

              {activeSection === 'compraventa' && compraventaVehs.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {(['todos', 'compra', 'moto'] as const).map((f) => (
                    <button
                      key={f}
                      onClick={() => setTipoFilter(f)}
                      className={`text-xs font-bold uppercase px-4 py-2 border transition-all cursor-pointer whitespace-nowrap ${
                        tipoFilter === f
                          ? 'bg-yellow-400 text-black border-yellow-400'
                          : 'bg-zinc-900/60 text-zinc-500 border-zinc-700 hover:border-zinc-500 hover:text-zinc-300'
                      }`}
                    >
                      {f === 'todos' ? 'Todos' : f === 'compra' ? 'Compra' : 'Motos'}
                    </button>
                  ))}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-zinc-900/60 border border-zinc-700 text-zinc-400 text-xs px-3 py-2 cursor-pointer outline-none hover:border-zinc-500 transition-colors"
                  >
                    <option value="default">Ordenar por</option>
                    <option value="price-asc">Precio: menor a mayor</option>
                    <option value="price-desc">Precio: mayor a menor</option>
                    <option value="km">Menor kilometraje</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Compraventa section */}
          {activeSection === 'compraventa' && (
            <>
              <p className="text-zinc-600 text-xs mb-6 font-medium">
                {filteredCompraventa.length} vehículo
                {filteredCompraventa.length !== 1 ? 's' : ''} disponible
                {filteredCompraventa.length !== 1 ? 's' : ''}
              </p>
              {filteredCompraventa.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-zinc-700 border border-dashed border-zinc-800">
                  <i className="ri-car-line text-5xl mb-4" />
                  <p className="text-sm font-bold uppercase tracking-wider">
                    Sin vehículos disponibles
                  </p>
                  <p className="text-xs mt-1 font-light">Próximamente nuevas incorporaciones</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {filteredCompraventa.map((v, i) => (
                    <VehicleCard
                      key={v.id}
                      v={v}
                      delay={i * 0.07}
                      onClick={() => setSelectedVehiculo(v)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Alquiler section */}
          {activeSection === 'alquiler' && (
            <>
              <div className="mb-6 p-4 bg-yellow-400/5 border border-yellow-400/15">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center text-yellow-400 flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-lg" />
                  </div>
                  <div>
                    <p className="text-yellow-400 text-xs font-bold uppercase tracking-wider mb-0.5">
                      Servicio de alquiler
                    </p>
                    <p className="text-zinc-500 text-xs leading-relaxed">
                      Alquila tu vehículo directamente en nuestro taller. Seguro incluido, entrega
                      y recogida en Ronda. Llámanos para consultar disponibilidad y condiciones.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-zinc-600 text-xs mb-6 font-medium">
                {alquilerVehs.length} vehículo{alquilerVehs.length !== 1 ? 's' : ''} disponible
                {alquilerVehs.length !== 1 ? 's' : ''} para alquiler
              </p>

              {alquilerVehs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-zinc-700 border border-dashed border-zinc-800">
                  <i className="ri-key-line text-5xl mb-4" />
                  <p className="text-sm font-bold uppercase tracking-wider">
                    Sin vehículos de alquiler disponibles
                  </p>
                  <p className="text-xs mt-1 font-light">Próximamente nuevas incorporaciones</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {alquilerVehs.map((v, i) => (
                    <RentalCard
                      key={v.id}
                      v={v}
                      delay={i * 0.07}
                      onClick={() => setSelectedVehiculo(v)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      {selectedVehiculo && (
        <VehicleDetailModal
          vehiculo={selectedVehiculo}
          onClose={() => setSelectedVehiculo(null)}
        />
      )}
    </>
  );
};

export default CarsListing;
